import { useCallback, useRef, useState } from 'react'
import { Zip, ZipDeflate, ZipPassThrough } from 'fflate'
import streamSaver from 'streamsaver'
import { useAuth0 } from '../../utils/auth'
import { apiRequest } from '../../utils/useApi'
import { useDetectionExplorerStore } from './DetectionExplorerStore'
import { extFromUrl, makeUniquePath, sanitizeSegment, toCSV, toFileTimestamp } from './exportFormat'
import { DetectionResult, GetDetectionsRequest, GetDetectionsResponseSchema } from './get_detections'
import { ALL_IMAGE_TYPES, ImageType } from './request_images'

export type ExportMode = 'detections' | 'images' | 'all'

// group_by_license_plate=false lets the backend honour the larger MAX_PAGE_SIZE (2000).
const EXPORT_PAGE_SIZE = 2000
const IMAGE_CONCURRENCY = 10

// Dev-only S3 CORS proxy (see vite.config.js). Override per backend env via VITE_IMAGE_PROXY_HOST.
const IMAGE_PROXY_HOST =
    import.meta.env.VITE_IMAGE_PROXY_HOST ?? 'be-stage-s3-ec1-debug-image-collection.s3.amazonaws.com'

// streamsaver needs its mitm iframe configured before the first download; do it lazily
// (and once) so importing this module has no `window` side effect.
let mitmConfigured = false
const configureStreamSaver = (): void => {
    if (mitmConfigured) return
    streamSaver.mitm = `${window.location.origin}/mitm.html`
    mitmConfigured = true
}

async function fetchAllPages(accessToken: string, request: GetDetectionsRequest): Promise<DetectionResult[]> {
    const all: DetectionResult[] = []
    let page = 1

    while (true) {
        const data = await apiRequest('POST', '/platform/get_detections', GetDetectionsResponseSchema, accessToken, {
            ...request,
            page,
            page_size: EXPORT_PAGE_SIZE,
            group_by_license_plate: false
        })
        all.push(...data.results)
        if (data.results.length < EXPORT_PAGE_SIZE || all.length >= data.total) break
        page++
    }

    return all
}

function toFetchUrl(url: string): string {
    if (!import.meta.env.DEV) return url
    try {
        const { hostname, pathname, search } = new URL(url)
        if (hostname === IMAGE_PROXY_HOST) return `/image-proxy${pathname}${search}`
    } catch {
        // fall through
    }
    return url
}

async function tryFetchBytes(url: string): Promise<Uint8Array | null> {
    try {
        // 'no-store' skips the HTTP cache: the gallery loads these URLs via <img> (no Origin
        // header), and reusing that cached non-CORS response would fail this CORS fetch.
        const res = await fetch(toFetchUrl(url), { cache: 'no-store' })
        if (!res.ok) return null
        return new Uint8Array(await res.arrayBuffer())
    } catch {
        return null
    }
}

function triggerDownload(blob: Blob, filename: string): void {
    const link = document.createElement('a')
    link.href = URL.createObjectURL(blob)
    link.download = filename
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
    URL.revokeObjectURL(link.href)
}

async function streamZipToFile(
    writer: WritableStreamDefaultWriter<Uint8Array>,
    detections: DetectionResult[],
    options: { csvContent?: string; imageTypes: ImageType[] }
): Promise<void> {
    const { csvContent, imageTypes } = options
    const wants = (type: ImageType) => imageTypes.includes(type)
    const uniquePath = makeUniquePath()

    await new Promise<void>((resolve, reject) => {
        let settled = false
        const settle = (fn: () => void) => {
            if (settled) return
            settled = true
            fn()
        }
        // The StreamSaver writer can already be errored (e.g. the user cancelled the save dialog),
        // in which case write()/close() throw or reject. Route those here so a broken stream rejects
        // cleanly instead of escaping as an unhandled rejection.
        const fail = (err: unknown) =>
            settle(() => {
                writer.abort().catch(() => {})
                reject(err instanceof Error ? err : new Error(String(err)))
            })
        const done = () => settle(() => resolve())

        const zip = new Zip((err, chunk, final) => {
            if (settled) return
            if (err) {
                fail(err)
                return
            }
            try {
                writer.write(chunk).catch(fail)
                if (final) {
                    writer.close().then(done, fail)
                }
            } catch (e) {
                fail(e)
            }
        })

        ;(async () => {
            try {
                if (csvContent) {
                    const csv = new ZipDeflate('detections.csv', { level: 6 })
                    zip.add(csv)
                    csv.push(new TextEncoder().encode(csvContent), true)
                }

                for (let i = 0; i < detections.length; i += IMAGE_CONCURRENCY) {
                    const batch = await Promise.all(
                        detections.slice(i, i + IMAGE_CONCURRENCY).map(async (d) => ({
                            d,
                            render:
                                wants('render') && d.images.render.url
                                    ? { url: d.images.render.url, bytes: await tryFetchBytes(d.images.render.url) }
                                    : null,
                            lp:
                                wants('lp') && d.images.lp.url
                                    ? { url: d.images.lp.url, bytes: await tryFetchBytes(d.images.lp.url) }
                                    : null,
                            roi:
                                wants('roi') && d.images.roi.url
                                    ? { url: d.images.roi.url, bytes: await tryFetchBytes(d.images.roi.url) }
                                    : null
                        }))
                    )
                    for (const { d, render, lp, roi } of batch) {
                        if (!render?.bytes && !lp?.bytes && !roi?.bytes) continue
                        const plate = sanitizeSegment(d.license_plate ?? 'unknown')
                        const dir = `station_${d.device_id}`
                        if (render?.bytes) {
                            const file = new ZipPassThrough(
                                uniquePath(`${dir}/${plate}_render.${extFromUrl(render.url)}`)
                            )
                            zip.add(file)
                            file.push(render.bytes, true)
                        }
                        if (lp?.bytes) {
                            const file = new ZipPassThrough(uniquePath(`${dir}/${plate}_lp.${extFromUrl(lp.url)}`))
                            zip.add(file)
                            file.push(lp.bytes, true)
                        }
                        if (roi?.bytes) {
                            const file = new ZipPassThrough(uniquePath(`${dir}/${plate}_roi.${extFromUrl(roi.url)}`))
                            zip.add(file)
                            file.push(roi.bytes, true)
                        }
                    }
                }

                zip.end()
            } catch (e) {
                fail(e)
            }
        })()
    })
}

export const useExportDetections = () => {
    const [isExporting, setIsExporting] = useState(false)
    // Synchronous guard against a double-export: state updates aren't committed until the
    // next render, so two rapid clicks could both pass an `isExporting` state check.
    const exportingRef = useRef(false)
    const { getAuthenticationData } = useAuth0()
    const filters = useDetectionExplorerStore((s) => s.filters)

    const exportDetections = useCallback(
        async (mode: ExportMode, selected?: DetectionResult[], imageTypes: ImageType[] = ALL_IMAGE_TYPES) => {
            if (exportingRef.current) return
            exportingRef.current = true
            setIsExporting(true)

            const baseName = `download-${filters.lot}-${toFileTimestamp(filters.from)}_to_${toFileTimestamp(filters.to)}`

            // createWriteStream must be called before any awaits — browser drops
            // the user-gesture context after the first await, silently blocking the download.
            let writer: WritableStreamDefaultWriter<Uint8Array> | null = null
            if (mode !== 'detections') {
                configureStreamSaver()
                writer = streamSaver.createWriteStream(`${baseName}.zip`).getWriter()
            }

            try {
                // Selected rows are already in memory; only the full export needs to page the API.
                const detections =
                    selected ?? (await fetchAllPages((await getAuthenticationData()).accessToken, filters))

                if (mode === 'detections') {
                    const blob = new Blob([toCSV(detections)], { type: 'text/csv;charset=utf-8;' })
                    triggerDownload(blob, `${baseName}.csv`)
                    return
                }

                // writer is always set for the image modes (created above before any await).
                if (!writer) throw new Error('Expected a writable stream for image export')
                await streamZipToFile(writer, detections, {
                    csvContent: mode === 'all' ? toCSV(detections) : undefined,
                    imageTypes
                })
            } catch (e) {
                // abort() rejects if the stream already errored; swallow so it isn't a second
                // unhandled rejection on top of the original failure.
                writer?.abort().catch(() => {})
                throw e
            } finally {
                exportingRef.current = false
                setIsExporting(false)
            }
        },
        [filters, getAuthenticationData]
    )

    return { exportDetections, isExporting }
}
